import { useState } from 'react';
import { Alert, Box, Link, Icon } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { ProductDataList } from '@nimbus-ds/patterns';
import { useModal } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { SelectedProductInterface } from 'domains/Catalog/Products/components';
import { SelectProductModal } from './SelectProductModal';
import {
  getProductsMaped,
  getSelectedProductsMaped,
  ProductsInterface,
} from './utils';

interface ProductsProps {
  products: ProductsInterface[];
  requiredText: string;
  onChange: (products: ProductsInterface[]) => void;
  selectLinkText?: string;
  editLinkText?: string;
  disabled?: boolean;
  excludedProductIds?: string[];
}

const PRODUCT_THUMBNAIL_PLACEHOLDER =
  'data:image/svg+xml,%3Csvg%20width%3D%2264%22%20height%3D%2264%22%20viewBox%3D%220%200%2064%2064%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Crect%20width%3D%2264%22%20height%3D%2264%22%20rx%3D%2212%22%20fill%3D%22%23F3F4F6%22/%3E%3Cpath%20d%3D%22M22%2025h-3c-1.1%200-2%20.9-2%202v18c0%201.1.9%202%202%202h26c1.1%200%202-.9%202-2V27c0-1.1-.9-2-2-2h-3l-2.4-3.6c-.38-.57-1.02-.92-1.7-.92h-10.8c-.68%200-1.32.35-1.7.92L22%2025z%22%20stroke%3D%22%23A0A4AC%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22/%3E%3Ccircle%20cx%3D%2232%22%20cy%3D%2236%22%20r%3D%226%22%20stroke%3D%22%23A0A4AC%22%20stroke-width%3D%222%22/%3E%3Ccircle%20cx%3D%2241%22%20cy%3D%2229%22%20r%3D%221.5%22%20fill%3D%22%23A0A4AC%22/%3E%3C/svg%3E';

export function Products({
  products,
  requiredText,
  onChange,
  selectLinkText,
  editLinkText,
  disabled = false,
  excludedProductIds,
}: Readonly<ProductsProps>): JSX.Element {
  const [isShowModal, openModal, closeModal] = useModal();
  const isEmpty = !products.length;
  const [refreshProducts, setRefreshProducts] = useState(false);

  const handleOnChange = (selecteds: SelectedProductInterface[]) => {
    onChange(getProductsMaped(selecteds));
  };

  const openProductsModal = () => {
    setRefreshProducts(true);
    openModal();
  };

  const t = useTranslationCatalog();
  const linkText = isEmpty
    ? selectLinkText ?? t('subscriptions.form.products.selector.select')
    : editLinkText ?? t('subscriptions.form.products.selector.edit');

  const handleRemove = (id: number) => {
    onChange(products.filter((current) => current.id !== id));
  };

  return (
    <>
      {(!isEmpty || !disabled) && (
        <Box display="flex" flexDirection="column" alignItems="stretch" gap="2">
          {!disabled && (
            <Link
              appearance="primary"
              as="button"
              textDecoration="none"
              onClick={openProductsModal}
            >
              <Icon source={<PlusCircleIcon />} color="primary-interactive" />
              {linkText}
            </Link>
          )}
          {!isEmpty && (
            <ProductDataList backgroundColor="neutral-background">
              <ProductDataList.Section link={undefined}>
                <ProductDataList.ItemDivider />
                {products.map((product, index) => (
                  <ProductDataList.Item
                    key={product.id}
                    id={String(product.id)}
                    title={product.description}
                    imageUrl={product.imageUrl || PRODUCT_THUMBNAIL_PLACEHOLDER}
                    imageAlt={product.description}
                    onRemove={
                      !disabled ? () => handleRemove(product.id) : undefined
                    }
                    withDivider={index !== products.length - 1}
                  />
                ))}
              </ProductDataList.Section>
            </ProductDataList>
          )}
        </Box>
      )}
      {!disabled && !!requiredText && (
        <Alert appearance="danger">{requiredText}</Alert>
      )}
      {!disabled && (
        <SelectProductModal
          isOpen={isShowModal}
          onClose={closeModal}
          selectedProducts={getSelectedProductsMaped(products)}
          onChange={handleOnChange}
          refreshProducts={refreshProducts}
          setRefreshProducts={setRefreshProducts}
          excludedProductIds={excludedProductIds}
        />
      )}
    </>
  );
}
